import * as PIXI from 'pixi.js';
import { StateManager } from '../../managers/StateManager';
import { boundsFromPoints, elementsInMarquee } from '../../utils/selection';
import { BRAND } from '../../utils/constants';
import { PixiAppRefs } from './types';

/** Colour of the rubber-band rectangle. */
const MARQUEE_COLOR = BRAND.GOLD;

/** Callbacks the InteractionManager drives while the user drags a selection box. */
export interface MarqueeController {
  onSelectStart: (screenX: number, screenY: number) => void;
  onSelectMove: (screenX: number, screenY: number) => void;
  onSelectEnd: (screenX: number, screenY: number) => void;
  onSelectClear: () => void;
}

/**
 * Draws the rubber-band rectangle (in screen space, on the stage) and, when the
 * drag ends, selects every connector and node the box touches. All coordinates
 * are canvas-relative screen pixels; the box is resolved to world space only at
 * the end, where it is matched against element positions.
 */
export function createMarqueeController(refs: PixiAppRefs, stateManager: StateManager): MarqueeController {
  let start = { x: 0, y: 0 };

  const ensureBox = (): PIXI.Graphics | null => {
    const app = refs.appManager.current?.getApp();
    if (!app) {
      return null;
    }
    if (!refs.selectionBox.current) {
      const box = new PIXI.Graphics();
      box.eventMode = 'none';
      app.stage.addChild(box);
      refs.selectionBox.current = box;
    }
    return refs.selectionBox.current;
  };

  const draw = (x: number, y: number): void => {
    const box = ensureBox();
    if (!box) {
      return;
    }
    const bounds = boundsFromPoints(start, { x, y });
    box.clear();
    box.rect(bounds.minX, bounds.minY, bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
    box.fill({ color: MARQUEE_COLOR, alpha: 0.1 });
    box.stroke({ width: 1, color: MARQUEE_COLOR, alpha: 0.8 });
  };

  return {
    onSelectStart: (screenX, screenY) => {
      start = { x: screenX, y: screenY };
      draw(screenX, screenY);
    },
    onSelectMove: (screenX, screenY) => {
      draw(screenX, screenY);
    },
    onSelectEnd: (screenX, screenY) => {
      refs.selectionBox.current?.clear();
      const viewport = refs.viewportManager.current;
      if (!viewport) {
        return;
      }
      const a = viewport.screenToWorld(start.x, start.y);
      const b = viewport.screenToWorld(screenX, screenY);
      const state = stateManager.getState();
      const ids = elementsInMarquee(boundsFromPoints(a, b), state.connectors, state.nodes, state.wires);
      stateManager.setSelection(ids);
    },
    onSelectClear: () => {
      stateManager.setSelection([]);
    }
  };
}
